import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import {
  PracticeRegionReservation,
  PracticeRegionSearchReservation,
} from "../model/practice-region-reservation";
import { DataService } from "./data.service";
import { SetSuppliesAditionalReservation } from "../model/set-supplies-aditional-reservation";
import { PracticeRegionFilter } from "../model/practice-region-filter";
import { PracticeRegionPreparationView } from "../model/practice-region-preparation-view";
import { PracticeFilter } from "../model/practice/practice-filter";
import { ReportViewer } from "../model/report-viewer";

@Injectable()
export class PracticeService {
  private readonly getPracticeRegionPreparationHtmlEndPoint: string =
    "/practice/getPracticeRegionPreparationHtml/";
  private readonly getPracticeRegionReservationEndPoint: string =
    "/practice/getPracticeRegionReservation/";
  private readonly getPracticeRegionSearchReservationEndPoint: string =
    "/practice/getPracticeRegionSearchReservation/";
  private readonly getFilteredPracticeRegionSearchReservationEndPoint: string =
    "/practice/getFilteredPracticeRegionSearchReservation/";
  private readonly getAditionalPracticeAndSetSuppliesReservationEndPoint: string =
    "/practice/GetAditionalPracticeAndSetSuppliesReservation/";
  private readonly getPracticesRegionFilterEndPoint: string =
    "/practice/getPracticesRegionFilter/";
  private readonly getPracticesFilterEndPoint: string =
    "/practice/getPracticesFilter/";
  private readonly getPosiblePracticeRegionSearchForValorizationEndPoint: string =
    "/practice/getPosiblePracticeRegionSearchForValorization/";
  private readonly getPracticesRegionFilterPhysicianEndPoint: string =
    "/practice/getPracticesRegionFilterPhysician/";
  private readonly getPracticesRegionFilterSectorIdEndPoint: string =
    "/practice/getPracticesRegionFilterSectorId/";
  private readonly getPreparationReportPdfEndPoint: string =
    "/practice/getPreparationReportPdf/";

  constructor(private dataService: DataService) {}

  async getPracticesRegionFilterSectorId(
    sectorId: number
  ): Promise<PracticeRegionFilter[]> {
    const params = { sectorId };
    return await this.dataService
      .get<PracticeRegionFilter[]>(
        this.getPracticesRegionFilterSectorIdEndPoint,
        params
      )
      .toPromise();
  }

    async getPreparationReportPdf(
      procedureIds: number[],
      centerId: number
    ): Promise<ReportViewer> {
      const param = { procedureIds, centerId };
      return await this.dataService
        .post<ReportViewer>(this.getPreparationReportPdfEndPoint, param)
        .toPromise();
    }

  async getPracticesRegionFilterPhysician(
    physicianId: number
  ): Promise<number[]> {
    const params = { physicianId };
    return await this.dataService
      .get<number[]>(this.getPracticesRegionFilterPhysicianEndPoint, params)
      .toPromise();
  }

  async getPracticeRegionPreparationView(
    practicesRegionIds?: number[]
  ): Promise<PracticeRegionPreparationView> {
    let ids: string;
    if (practicesRegionIds) {
      ids = JSON.stringify(practicesRegionIds);
    }
    const params = { practicesRegionIds: ids };
    return await this.dataService
      .get<PracticeRegionPreparationView>(
        this.getPracticeRegionPreparationHtmlEndPoint,
        params
      )
      .toPromise();
  }

  async getPracticesRegionFilter(): Promise<PracticeRegionFilter[]> {
    const params = {};
    return await this.dataService
      .get<PracticeRegionFilter[]>(
        this.getPracticesRegionFilterEndPoint,
        params
      )
      .toPromise();
  }

  async getPracticesFilter(): Promise<PracticeFilter[]> {
    const params = {};
    return await this.dataService
      .get<PracticeFilter[]>(this.getPracticesFilterEndPoint, params)
      .toPromise();
  }

  async getPosiblePracticeRegionSearchForValorization(
    planId: number,
    medicalAppointmentId: number,
    excludePracticesRegionIds?: number[]
  ): Promise<PracticeRegionSearchReservation[]> {
    let ids: string;
    if (excludePracticesRegionIds) {
      ids = JSON.stringify(excludePracticesRegionIds);
    }

    const params = {
      planId: planId,
      medicalAppointmentId: medicalAppointmentId,
      excludePracticesRegionIds: ids,
    };
    return await this.dataService
      .get<PracticeRegionSearchReservation[]>(
        this.getPosiblePracticeRegionSearchForValorizationEndPoint,
        params
      )
      .toPromise();
  }

  async getFilteredPracticeRegionSearchReservation(
    planId: number,
    physicianId?: number,
    equipmentId?: number,
    centerId?: number
  ): Promise<PracticeRegionSearchReservation[]> {
    const params = { planId, physicianId, equipmentId, centerId };
    return await this.dataService
      .get<PracticeRegionSearchReservation[]>(
        this.getFilteredPracticeRegionSearchReservationEndPoint,
        params
      )
      .toPromise();
  }

  async getPracticesRegionSearchReservation(
    planId: number,
    excludePracticesRegionIds?: number[],
    centerId?: number
  ): Promise<PracticeRegionSearchReservation[]> {
    let ids: string;
    if (excludePracticesRegionIds) {
      ids = JSON.stringify(excludePracticesRegionIds);
    }
    const params = {
      planId: planId,
      excludePracticesRegionIds: ids,
      centerId: centerId,
    };
    return await this.dataService
      .get<PracticeRegionSearchReservation[]>(
        this.getPracticeRegionSearchReservationEndPoint,
        params
      )
      .toPromise();
  }

  async getAditionalPracticeAndSetSuppliesReservation(
    practiceRegionIds: number[],
    planId: number,
    centerId?: number
  ): Promise<SetSuppliesAditionalReservation[]> {
    const params = {
      practiceRegionIds: JSON.stringify(practiceRegionIds),
      planId: planId,
      centerId: centerId,
    };
    return await this.dataService
      .get<SetSuppliesAditionalReservation[]>(
        this.getAditionalPracticeAndSetSuppliesReservationEndPoint,
        params
      )
      .toPromise();
  }

  getPracticeRegionReservation(
    planId: number,
    name: string,
    excludePracticesIds: number[],
    principalPracticeRegionId?: number,
    centerId?: number
  ): Observable<PracticeRegionReservation[]> {
    const params = {
      planId: planId,
      name: name,
      excludePracticesIds: JSON.stringify(excludePracticesIds),
      principalPracticeRegionId: principalPracticeRegionId,
      centerId: centerId,
    };

    return this.dataService.get<PracticeRegionReservation[]>(
      this.getPracticeRegionReservationEndPoint,
      params
    );
  }
}
